import { AuthenticationPage, useAuth } from '@ais-ui/auth';
import { SessionProvider } from '@ais-ui/websockets';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { MapPage } from '../components/MapPage';
import { Sidebar } from '../components/sidebar/Sidebar';
import { FilterProvider } from '../modules/Map/FilterContext';
import { ROUTES } from '../routes';
import { getAuthRouteView } from './getAuthRouteView';
import {
  SettingsProvider,
  useSettings,
} from '../modules/Settings/SettingsProvider';
import { PermissionsProvider } from '../modules/Permissions/PermissionsProvider';
import { PortProvider } from '../modules/Ports';
import { MapPanelProvider } from '../modules/Map/MapPanel';
import { NotificationProvider } from '../modules/Notifications/NotificationContext';
import { FullScreenLoadingSpinner, TooltipProvider } from '@ais-ui/ui-core';

import { ReportProvider } from '../modules/Reports/ReportContext';
import { ReleaseNotes } from '../modules/ReleaseNotes/ReleaseNotes';
import { ReplayProvider } from '../modules/Replay/ReplayContext';
import { TimeZoneProvider } from '@ais-ui/utils';
import { CoverageProvider } from '../modules/Map/Coverage/CoverageContext';
import { ReleaseNotesProvider } from '../modules/ReleaseNotes/ReleaseNotesProvider';
import {
  useViewMode,
  ViewMode,
  ViewModeProvider,
} from '../components/ViewModeProvider';
import React, { Suspense, useMemo } from 'react';
import { ClickToComponent } from 'click-to-react-component';
import { SelectionStateProvider } from '../components/SelectionStateProvider';
import { useWhitelabel } from '../configuration/useWhitelabel';
import { RecentSearchesProvider } from '../components/RecentSearchesProvider';
import { AsyncJobsProvider } from '@ais-ui/async-jobs';
import { ReportExportManager } from '../modules/Reports/ReportExportManager';
import { GPSLostProvider } from '../modules/Map/GpsLost/GPSLostContext';
import { WeatherLegendProvider } from '../modules/Map/WeatherLegend';
import { ColorByPinProvider } from '../modules/Map/ColorTargetsBy';
import { ProjectionProvider } from '@ais-ui/react-openlayers';
import { ResponsiveLayout } from '../components/layouts/ResponsiveLayout';
import { ThemeWrapper } from '../configuration/ThemeWrapper';
import { ServiceAvailabilityProvider } from '@ais-ui/api';

const SettingsPage = React.lazy(() =>
  import('../modules/Settings/SettingsPage').then((m) => ({
    default: m.SettingsPage,
  })),
);
const SettingsPageProvider = React.lazy(() =>
  import('../modules/Settings/SettingsPageProvider').then((m) => ({
    default: m.SettingsPageProvider,
  })),
);
const DetailsPage = React.lazy(() =>
  import('../components/DetailsPage/DetailsPage').then((m) => ({
    default: m.DetailsPage,
  })),
);
const MultiFactorPage = React.lazy(() =>
  import('../modules/MultiFactor/MultiFactorPage').then((m) => ({
    default: m.MultiFactorPage,
  })),
);
const ChangePasswordPage = React.lazy(() =>
  import('../modules/ChangePasswordPage').then((m) => ({
    default: m.ChangePasswordPage,
  })),
);
const HelpPage = React.lazy(() =>
  import('../components/HelpPage/HelpPage').then((m) => ({
    default: m.HelpPage,
  })),
);

// We've created this component to avoid rerenders of the providers
//  when activeComponent is changed
const AppContent: React.FC = () => {
  const location = useLocation();
  const { viewMode } = useViewMode();

  const element = useMemo(() => {
    if (
      viewMode === ViewMode.Replay ||
      viewMode === ViewMode.Normal ||
      viewMode === ViewMode.Drawing ||
      viewMode === ViewMode.Kiosk
    ) {
      return <MapPage></MapPage>;
    } else if (viewMode === ViewMode.Detail) {
      return (
        <Suspense fallback={<FullScreenLoadingSpinner />}>
          <DetailsPage></DetailsPage>
        </Suspense>
      );
    } else {
      throw new Error(`Unknown view mode: ${viewMode}`);
    }
  }, [viewMode]);

  if (location.pathname.includes(ROUTES.SETTINGS.$path())) {
    return (
      <Suspense fallback={<FullScreenLoadingSpinner />}>
        <Routes>
          <Route
            path={`${ROUTES.SETTINGS.$path()}/*`}
            element={
              <SettingsPageProvider>
                <SettingsPage></SettingsPage>
              </SettingsPageProvider>
            }
          />
        </Routes>
      </Suspense>
    );
  }

  if (location.pathname.includes(ROUTES.MULTIFACTOR.AUTHENTICATED.$path())) {
    return (
      <Suspense fallback={<FullScreenLoadingSpinner />}>
        <Routes>
          <Route
            path={ROUTES.MULTIFACTOR.AUTHENTICATED.$path()}
            element={<MultiFactorPage authenticated={true}></MultiFactorPage>}
          />
          ;
        </Routes>
      </Suspense>
    );
  }

  const isMinimalUI =
    viewMode === ViewMode.Drawing || viewMode === ViewMode.Kiosk;

  return (
    <ResponsiveLayout
      desktopLayout={
        <div className="h-dvh w-screen flex overflow-x-hidden">
          {!isMinimalUI && (
            <div className="flex-shrink">
              <Sidebar id="main-sidebar" />
            </div>
          )}
          <div className="relative flex-grow">
            <Routes>
              <Route path={`${ROUTES.MAP.$path()}/*`} element={element} />
            </Routes>
          </div>
        </div>
      }
    >
      <Routes>
        <Route path={`${ROUTES.MAP.$path()}/*`} element={element} />
      </Routes>
    </ResponsiveLayout>
  );
};

const TimeZoneRoute = () => {
  const { settings, dataUpdatedAt } = useSettings();

  // Get the projection setting from ribbonPlugin settings, default to EPSG:3857 if not set
  const displayProjection =
    settings['ribbonPlugin.settings']?.display_projection ?? 'EPSG:3857';

  return (
    <ThemeWrapper>
      <TimeZoneProvider settings={settings} dataUpdatedAt={dataUpdatedAt}>
        <ProjectionProvider displayProjection={displayProjection}>
          <TooltipProvider>
            <AsyncJobsProvider>
              <ReportExportManager />
              <NotificationProvider>
                <SelectionStateProvider>
                  <RecentSearchesProvider>
                    <ViewModeProvider>
                      <FilterProvider>
                        <ReportProvider>
                          <ServiceAvailabilityProvider>
                            <PortProvider>
                              <CoverageProvider>
                                <GPSLostProvider>
                                  <WeatherLegendProvider>
                                    <ColorByPinProvider>
                                      <MapPanelProvider>
                                        <ReleaseNotesProvider>
                                          <AppContent />
                                          <ReleaseNotes />
                                        </ReleaseNotesProvider>
                                      </MapPanelProvider>
                                    </ColorByPinProvider>
                                  </WeatherLegendProvider>
                                </GPSLostProvider>
                              </CoverageProvider>
                            </PortProvider>
                          </ServiceAvailabilityProvider>
                        </ReportProvider>
                      </FilterProvider>
                    </ViewModeProvider>
                  </RecentSearchesProvider>
                </SelectionStateProvider>
              </NotificationProvider>
            </AsyncJobsProvider>
          </TooltipProvider>
        </ProjectionProvider>
      </TimeZoneProvider>
    </ThemeWrapper>
  );
};

const AuthenticatedRoute: React.FC = () => {
  const authContext = useAuth();
  const { isAuthenticated, isInitializing } = authContext;
  const location = useLocation(); // Get current location

  const routeView = getAuthRouteView({
    isInitializing,
    isAuthenticated,
    pathname: location.pathname,
    loginPath: ROUTES.LOGIN.$path(),
    mapPath: ROUTES.MAP.$path(),
  });

  if (routeView.view === 'loading') {
    return <FullScreenLoadingSpinner />;
  }

  if (routeView.view === 'redirect') {
    return <Navigate to={routeView.to} replace={routeView.replace} />;
  }

  return (
    <SessionProvider
      logout={authContext.logout}
      reconnect={authContext.reconnect}
    >
      <PermissionsProvider>
        <ReplayProvider>
          <SettingsProvider>
            <TimeZoneRoute></TimeZoneRoute>
          </SettingsProvider>
        </ReplayProvider>
      </PermissionsProvider>
    </SessionProvider>
  );
};

function AuthenticationPageWithWhitelabel() {
  const { whitelabelImagePaths, whitelabelTexts } = useWhitelabel();
  return (
    <AuthenticationPage
      whitelabel={{
        signInImagePath: whitelabelImagePaths.signInPath,
        testimonialText: whitelabelTexts?.testimonial?.text,
        testimonialAuthor: whitelabelTexts?.testimonial?.author,
      }}
    />
  );
}

export function App() {
  return (
    <div>
      <ClickToComponent />
      <Routes>
        <Route
          path={ROUTES.HELP.$path()}
          element={
            <Suspense fallback={<FullScreenLoadingSpinner />}>
              <HelpPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.LOGIN.$path()}
          element={<AuthenticationPageWithWhitelabel />}
        />
        <Route
          path={ROUTES.MULTIFACTOR.BEFORE_LOGIN.$path()}
          element={
            <Suspense fallback={<FullScreenLoadingSpinner />}>
              <MultiFactorPage authenticated={false}></MultiFactorPage>
            </Suspense>
          }
        />
        <Route
          path={ROUTES.AUTHENTICATION.CHANGE_PASSWORD.$path()}
          element={
            <Suspense fallback={<FullScreenLoadingSpinner />}>
              <ChangePasswordPage></ChangePasswordPage>
            </Suspense>
          }
        />
        <Route path="*" element={<AuthenticatedRoute />} />
      </Routes>
    </div>
  );
}

export default App;
